import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { Distribution, FoundationLevel, LEVEL_COLORS } from '../data/competencyExplorerUtils';

// ── "How is this calculated?" info popover ────────────────────────────────────
export const CalcInfo: React.FC<{ title: string; lines: string[]; align?: 'left' | 'right' }> = ({
  title, lines, align = 'right',
}) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold transition ${open ? 'text-indigo-700' : 'text-gray-500 hover:text-indigo-600'}`}
        title="How is this calculated?"
      >
        <Info size={13} /> How it's calculated
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute z-50 top-6 ${align === 'right' ? 'right-0' : 'left-0'} w-80 max-w-[85vw] rounded-xl border border-gray-200 bg-white shadow-xl p-3.5`}>
            <p className="text-[13px] font-bold text-gray-800 mb-2">{title}</p>
            <ul className="space-y-1.5">
              {lines.map((l, i) => (
                <li key={i} className="text-[12px] text-gray-700 leading-relaxed flex gap-1.5">
                  <span className="text-indigo-400 mt-0.5 shrink-0">•</span>
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </span>
  );
};

// ── compact count formatting (national scale: 112662 → "1.1L", 58103 → "58.1K") ──
export const fmtCount = (n: number): string => {
  if (n >= 100000) return `${(n / 100000).toFixed(n >= 1000000 ? 0 : 1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

// ── movement badge (Entry → Exit delta) ──────────────────────────────────────
export const MovementBadge: React.FC<{ delta: number; size?: 'sm' | 'md' }> = ({ delta, size = 'sm' }) => {
  const text = size === 'md' ? 'text-sm' : 'text-[13px]';
  if (!delta) return <span className={`${text} font-semibold text-gray-600`}>±0%</span>;
  const up = delta > 0;
  return (
    <span className={`${text} font-bold inline-flex items-center gap-0.5 ${up ? 'text-emerald-600' : 'text-rose-500'}`}>
      {up ? '▲' : '▼'} {Math.abs(delta)}%
    </span>
  );
};

// ── level pill ────────────────────────────────────────────────────────────────
export const LevelPill: React.FC<{ level: FoundationLevel }> = ({ level }) => (
  <span
    className="px-2 py-0.5 rounded-full text-[12px] font-bold"
    style={{ background: `${LEVEL_COLORS[level]}18`, color: LEVEL_COLORS[level] }}
  >
    {level}
  </span>
);

// ── thin segmented B/P/Pr stacked bar ─────────────────────────────────────────
export const StackedBPrBar: React.FC<{ dist: Distribution; height?: number; showLabels?: boolean }> = ({
  dist, height = 10, showLabels = false,
}) => {
  if (!dist.total) {
    return <div className="w-full rounded-full bg-gray-100" style={{ height }} />;
  }
  const segs = [
    { pct: dist.prPct, color: LEVEL_COLORS.Proficient,  label: 'Proficient' },
    { pct: dist.pPct,  color: LEVEL_COLORS.Progressing, label: 'Progressing' },
    { pct: dist.bPct,  color: LEVEL_COLORS.Beginner,    label: 'Beginner' },
  ];
  return (
    <div>
      <div className="w-full rounded-full overflow-hidden flex bg-gray-100" style={{ height }}>
        {segs.map((s, i) => s.pct > 0 && (
          <div
            key={i}
            className="h-full transition-all duration-500"
            style={{ width: `${s.pct}%`, background: s.color }}
            title={`${s.label}: ${s.pct}%`}
          />
        ))}
      </div>
      {showLabels && (
        <div className="flex justify-between mt-1 text-[11px] font-semibold">
          <span style={{ color: LEVEL_COLORS.Proficient }}>Pr {dist.prPct}%</span>
          <span style={{ color: LEVEL_COLORS.Progressing }}>Pg {dist.pPct}%</span>
          <span style={{ color: LEVEL_COLORS.Beginner }}>Bg {dist.bPct}%</span>
        </div>
      )}
    </div>
  );
};

// ── dual Entry vs Exit proficiency bars ───────────────────────────────────────
export const EntryExitBars: React.FC<{ entry: Distribution; exit: Distribution; compact?: boolean }> = ({
  entry, exit, compact = false,
}) => {
  const has = entry.total > 0 || exit.total > 0;
  const barH = compact ? 'h-2' : 'h-2.5';
  const Row: React.FC<{ label: string; dist: Distribution; color: string }> = ({ label, dist, color }) => (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-gray-600 w-8 shrink-0">{label}</span>
      <div className={`flex-1 bg-gray-100 rounded-full ${barH} overflow-hidden`}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${dist.prPct}%`, background: color }} />
      </div>
      <span className="text-[12px] font-bold text-gray-700 w-9 text-right">{has ? `${dist.prPct}%` : '—'}</span>
    </div>
  );
  return (
    <div className="space-y-1">
      <Row label="Entry" dist={entry} color="#94A3B8" />
      <Row label="Exit"  dist={exit}  color="#6366F1" />
    </div>
  );
};

// ── full B/P/Pr breakdown (three labelled rows) ───────────────────────────────
export const BPrBreakdown: React.FC<{ dist: Distribution; title?: string }> = ({ dist, title }) => {
  const rows: { label: FoundationLevel; v: number; c: number }[] = [
    { label: 'Proficient',  v: dist.prPct, c: dist.proficient },
    { label: 'Progressing', v: dist.pPct,  c: dist.progressing },
    { label: 'Beginner',    v: dist.bPct,  c: dist.beginner },
  ];
  return (
    <div className="flex-1 min-w-0">
      {title && <p className="text-[12px] font-semibold text-gray-700 mb-1.5">{title} · {fmtCount(dist.total)} students</p>}
      {dist.total === 0 ? (
        <p className="text-[13px] text-gray-500 italic py-2">No data</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map(r => (
            <div key={r.label} className="flex items-center gap-2">
              <span className="text-[12px] font-medium w-[70px] shrink-0" style={{ color: LEVEL_COLORS[r.label] }}>{r.label}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${r.v}%`, background: LEVEL_COLORS[r.label] }} />
              </div>
              <span className="text-[12px] text-gray-600 w-16 text-right shrink-0">{r.v}% ({fmtCount(r.c)})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── segmented tab / pill switcher ─────────────────────────────────────────────
interface SegmentedProps<T extends string> {
  options: { key: T; label: string; icon?: React.ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  size?: 'sm' | 'md';
}
export function Segmented<T extends string>({ options, value, onChange, size = 'md' }: SegmentedProps<T>) {
  const pad = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';
  return (
    <div className="inline-flex bg-gray-100 rounded-xl p-1 gap-1">
      {options.map(o => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`${pad} rounded-lg font-semibold transition-all inline-flex items-center gap-1.5 ${
            value === o.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-700 hover:text-gray-700'
          }`}
        >
          {o.icon}{o.label}
        </button>
      ))}
    </div>
  );
}

// ── legend ────────────────────────────────────────────────────────────────────
export const BPrLegend: React.FC = () => (
  <div className="flex items-center gap-4 text-[12px] font-semibold text-gray-700">
    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: LEVEL_COLORS.Proficient }} />Proficient ≥83%</span>
    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: LEVEL_COLORS.Progressing }} />Progressing 50–83%</span>
    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: LEVEL_COLORS.Beginner }} />Beginner &lt;50%</span>
  </div>
);
